import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGameStore } from '../../store/gameStore';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { 
  Home, 
  Users, 
  MapPin, 
  ClipboardList, 
  BarChart3, 
  Target,
  Activity,
  Eye,
  LogOut,
  Settings,
  Crown,
  MessageSquare
} from 'lucide-react';
import { BrandLogo } from '../shared/BrandLogo';
import ShareReferralChip from '../shared/ShareReferralChip';

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTeamId } = useGameStore();
  const { user, logout, isCoach, subscriptionTier, subscriptionActive, isAdmin } = useAuthStore();
  
  const navItems = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/lineup', label: 'Lineup', icon: Users, requiresTeam: true },
    { path: '/field', label: 'Field', icon: MapPin, requiresTeam: true },
    { path: '/scoring', label: 'Scoring', icon: ClipboardList, requiresTeam: true },
    { path: '/pitching', label: 'Pitching', icon: Activity, requiresTeam: true },
    { path: '/stats', label: 'Stats', icon: BarChart3, requiresTeam: true },
    { path: '/spray-charts', label: 'Spray', icon: Target, requiresTeam: true },
    { path: '/coaches', label: 'Coaches', icon: Settings, requiresTeam: true },
    { path: '/messages', label: 'Messages', icon: MessageSquare, requiresTeam: true },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <nav className="text-white shadow-lg sticky top-0 z-50"
      style={{
        background: 'linear-gradient(180deg, #0A1322 0%, #131E36 100%)',
        borderBottom: '1px solid rgba(212, 160, 58, 0.25)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Tagline */}
          <div className="flex items-center gap-3" data-testid="tagline-container">
            <span className="text-base font-bold italic tracking-wide" style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#F5C455',
              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }}>
              Score Smarter, Not Harder
            </span>
          </div>

          {/* Logo + Wordmark — uses the official 3D DS monogram */}
          <Link to="/dashboard" className="flex items-center" data-testid="logo-link">
            <BrandLogo size={36} withWordmark />
          </Link>

          {/* Mobile-only: share chip in the top bar (desktop chip lives next to user info below) */}
          {user && (
            <div className="lg:hidden">
              <ShareReferralChip />
            </div>
          )}

          {/* Navigation Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map(({ path, label, icon: Icon, requiresTeam }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  isActive(path)
                    ? 'bg-ds-emerald/25 text-white ring-1 ring-ds-emerald/40'
                    : requiresTeam && !currentTeamId
                    ? 'text-slate-500 cursor-not-allowed opacity-50'
                    : 'text-slate-300 hover:bg-white/8 hover:text-white'
                }`}
                onClick={(e) => {
                  if (requiresTeam && !currentTeamId) e.preventDefault();
                }}
              >
                <Icon size={16} />
                <span>{label}</span>
              </Link>
            ))}
            
            {/* User info and logout */}
            {user && (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-ds-gold-soft/40">
                {/* Share & earn chip — visible from every authenticated page */}
                <ShareReferralChip />

                {/* Subscription/Admin badge */}
                {isAdmin ? (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold tracking-wide"
                    style={{ background: 'rgba(212, 160, 58, 0.15)', color: '#F5C455', border: '1px solid #B88C2E' }}>
                    ADMIN
                  </span>
                ) : subscriptionActive ? (
                  <span 
                    className={`text-xs px-2 py-0.5 rounded-full font-bold tracking-wide ${
                      subscriptionTier === 'pro' 
                        ? 'text-amber-200' 
                        : 'text-emerald-200'
                    }`}
                    style={{
                      background: subscriptionTier === 'pro' ? 'rgba(212, 160, 58, 0.2)' : 'rgba(31, 168, 90, 0.18)',
                      border: subscriptionTier === 'pro' ? '1px solid #B88C2E' : '1px solid #1FA85A',
                    }}
                  >
                    {subscriptionTier === 'pro' ? 'PRO' : 'COACH'}
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/subscription')}
                    data-testid="upgrade-btn"
                    className="text-ds-gold-bright hover:text-ds-gold hover:bg-white/10"
                  >
                    <Crown size={16} className="mr-1" />
                    Upgrade
                  </Button>
                )}
                <span className="text-sm text-slate-300">{user.username}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-slate-300 hover:text-white hover:bg-white/10"
                >
                  <LogOut size={16} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden overflow-x-auto pb-2 px-2">
        <div className="flex gap-1 min-w-max">
          {navItems.map(({ path, label, icon: Icon, requiresTeam }) => (
            <Link
              key={path}
              to={path}
              data-testid={`nav-mobile-${label.toLowerCase()}`}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                isActive(path)
                  ? 'bg-ds-emerald/25 text-white ring-1 ring-ds-emerald/40'
                  : requiresTeam && !currentTeamId
                  ? 'text-slate-500 cursor-not-allowed opacity-50'
                  : 'text-slate-300 hover:bg-white/8 hover:text-white'
              }`}
              onClick={(e) => {
                if (requiresTeam && !currentTeamId) e.preventDefault();
              }}
            >
              <Icon size={14} />
              <span>{label}</span>
            </Link>
          ))}
          {user && (
            <button onClick={handleLogout} data-testid="mobile-logout-btn"
              className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap text-red-300 hover:bg-red-900/30">
              <LogOut size={14} />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};
